import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faBold,
  faH1,
  faItalic,
  faListOl,
  faListUl,
  faUnderline,
  type IconDefinition,
} from '@fortawesome/sharp-solid-svg-icons';
import { Editor, EditorState, Modifier, RichUtils } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import 'draft-js/dist/Draft.css';
import { Button } from 'eone-ui/buttons/button';
import { getDataTestId } from 'eone-ui/functions/data-test-ids';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { SCView } from '../view';
import type { SCFormFieldProps } from './form_field';
import { SCFormField } from './form_field';

interface SCFormFieldRichTextEditorProps extends SCFormFieldProps {
  AutoFocus?: boolean;
  Icon?: IconProp;
  GlobalVariables?: { Label: string; Data: any }[];
  SourceColumns?: { Label: string; Data: any }[];
  Placeholder?: string;
  DataTestId?: string;
}

const SCFormFieldRichTextEditor = ({
  label,
  name,
  required,
  disabled,
  rules,
  visible,
  defaultValue,
  GlobalVariables,
  Placeholder,
  SourceColumns,
  DataTestId,
}: SCFormFieldRichTextEditorProps) => {
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const { setValue, getValues } = useFormContext();
  const initialised = useRef(false);

  const toggleInlineStyle = useCallback(
    (style: string) => {
      setEditorState(RichUtils.toggleInlineStyle(editorState, style));
    },
    [editorState],
  );

  const toggleBlockType = useCallback(
    (blockType: string) => {
      setEditorState(RichUtils.toggleBlockType(editorState, blockType));
    },
    [editorState],
  );

  useEffect(() => {
    const state = stateFromHTML(getValues(name) ?? '');
    const newEditorState = EditorState.push(editorState, state, 'insert-characters');
    setEditorState(newEditorState);
    initialised.current = true;
  }, []);

  useEffect(() => {
    if (initialised.current == false) return;
    setValue(name, stateToHTML(editorState.getCurrentContent()));
  }, [editorState]);

  const insert = useCallback(
    (value: string) => {
      const currentContentState = editorState.getCurrentContent();
      const selectionState = editorState.getSelection();
      const newContentState = Modifier.insertText(currentContentState, selectionState, value);
      const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
      setEditorState(newEditorState);
    },
    [editorState],
  );

  const controlButtons: {
    Icon?: IconDefinition;
    Label: string;
    TestIdSegment: string;
    MenuItems?: { Label: string; Data: any }[];
    OnClick?: any;
  }[] = useMemo(() => {
    const buttons = [
      {
        Label: '',
        Icon: faBold,
        TestIdSegment: 'bold',
        OnClick: () => toggleInlineStyle('BOLD'),
      },
      {
        Label: '',
        Icon: faItalic,
        TestIdSegment: 'italic',
        OnClick: () => toggleInlineStyle('ITALIC'),
      },
      {
        Label: '',
        Icon: faUnderline,
        TestIdSegment: 'underline',
        OnClick: () => toggleInlineStyle('UNDERLINE'),
      },
      {
        Label: '',
        Icon: faH1,
        TestIdSegment: 'h1',
        OnClick: () => toggleBlockType('header-one'),
      },
      {
        Label: '',
        Icon: faListUl,
        TestIdSegment: 'unordered-list',
        OnClick: () => toggleBlockType('unordered-list-item'),
      },
      {
        Label: '',
        Icon: faListOl,
        TestIdSegment: 'ordered-list',
        OnClick: () => toggleBlockType('ordered-list-item'),
      },
    ];
    if (GlobalVariables != undefined) {
      buttons.push({
        Label: 'Global Variables',
        TestIdSegment: 'global-variables',
        OnClick: insert,
        MenuItems: GlobalVariables,
      });
    }
    if (SourceColumns != undefined) {
      buttons.push({
        Label: 'Source Columns',
        TestIdSegment: 'source-columns',
        OnClick: insert,
        MenuItems: SourceColumns,
      });
    }
    return buttons;
  }, [toggleInlineStyle, toggleBlockType, SourceColumns, GlobalVariables]);

  const buttons = useMemo(() => {
    if (controlButtons === undefined || controlButtons === null || controlButtons.length === 0) {
      return null;
    }
    return controlButtons.map((button, index) => {
      return (
        <Button
          key={index}
          Label={button.Label}
          Icon={button.Icon}
          MenuItems={button.MenuItems}
          OnClick={button.OnClick}
          DataTestId={
            DataTestId ? getDataTestId(DataTestId, 'toolbar', button.TestIdSegment) : undefined
          }
        />
      );
    });
  }, [controlButtons, DataTestId]);

  const handleKeyCommand = useCallback((command: string, editorState: EditorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  }, []);

  return (
    <SCFormField
      visible={visible}
      label={label}
      name={name}
      disabled={disabled}
      required={required || !!rules?.required}
      rules={rules}
      defaultValue={defaultValue}
    >
      {() => {
        return (
          <SCView flex flexRow wFull justifyEnd itemsEnd>
            <SCView flex flex1>
              <SCView wFull>
                {label && (
                  <div className="eone-ui-form-label-container">
                    <label className="eone-ui-form-label">
                      <span>
                        {label}
                        {required && '*'}
                      </span>
                    </label>
                  </div>
                )}
                <div style={{ border: '1px solid #ccc' }} data-testid={DataTestId}>
                  <div
                    style={{
                      display: 'flex',
                      borderBottom: '1px solid #ccc',
                      flexDirection: 'row',
                      gap: 10,
                      padding: 10,
                    }}
                    data-testid={DataTestId ? getDataTestId(DataTestId, 'toolbar') : undefined}
                  >
                    {buttons}
                  </div>
                  <div
                    style={{
                      paddingLeft: 10,
                      paddingRight: 10,
                      paddingTop: 10,
                      minHeight: 100,
                      maxHeight: 200,
                      overflow: 'scroll',
                    }}
                    data-testid={DataTestId ? getDataTestId(DataTestId, 'editor') : undefined}
                  >
                    <Editor
                      editorState={editorState}
                      onChange={setEditorState}
                      handleKeyCommand={handleKeyCommand}
                      placeholder={Placeholder ?? 'Type here'}
                    />
                  </div>
                </div>
              </SCView>
            </SCView>
          </SCView>
        );
      }}
    </SCFormField>
  );
};

export { SCFormFieldRichTextEditor };
export type { SCFormFieldRichTextEditorProps };
